import { useState } from "react";
import s from "./Programme.module.css";
import { Page } from "../../layout/AppShell";
import { useProgramme } from "../../api/queries";
import { byDesk, decidedFraction, type Programme } from "../../domain/programme";
import { Button, Card, EmptyState, ErrorState, Segmented, Skeleton } from "../../design/components";
import { EvidenceChain } from "./EvidenceChain";
import { formatDateTime } from "../../lib/format";
import { locale, t, tn } from "../../i18n";

const YEARS = [0, 1, 2].map((n) => new Date().getUTCFullYear() + n);

/**
 * The recommendation: what to do, when, and what nobody can decide yet.
 *
 * This is the screen the other seven argue towards. Money, traffic, weather and
 * crew are four separate questions on four separate screens; here they are one
 * answer with dates on it.
 *
 * THE THIRD BUCKET IS THE PRODUCT. Any tool can emit a plan — a priority list,
 * a Gantt chart, a five-year programme. None of them emit the decisions they
 * declined to make, because a plan with holes demonstrates badly. It is
 * nevertheless the only kind that survives an estate whose data is half-loaded,
 * and every estate's is. So "could not be decided" gets a full block, not a
 * footnote, and it is grouped BY DESK with a money figure against each —
 * turning eleven missing facts into two phone calls with an amount attached.
 */
export function ProgrammePage() {
  const [year, setYear] = useState(YEARS[1]);
  const programme = useProgramme(year);

  return (
    <Page
      eyebrow="Deep thinking"
      title="What to do next"
      actions={
        <Segmented
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          ariaLabel="Fiscal year"
          options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
        />
      }
    >
      {programme.isPending && <Skeleton rows={7} />}
      {programme.isError && <ErrorState error={programme.error} retry={() => programme.refetch()} />}
      {programme.data && <Body p={programme.data} />}
    </Page>
  );
}

const money = (n: number, cur?: string) => `${cur ? cur + " " : ""}${Math.round(n).toLocaleString(locale())}`;

function Body({ p }: { p: Programme }) {
  /* Which recommendation the reader is asking "why?" about. One at a time:
     the chain is a page of reading and stacking several turns the programme
     into a wall. */
  const [asking, setAsking] = useState<string | null>(null);
  if (!p.available) {
    return <EmptyState>{p.whyNot ?? "No programme could be built for this year."}</EmptyState>;
  }

  const blocked = p.totals.blockedByMissingFacts;
  const decided = decidedFraction(p);
  const desks = byDesk(p.undecidable);

  return (
    <>
      <div data-testid="programme-headline" className={`${s.headline} ${blocked > 0 ? s.blocked : s.clean}`}>
        <div className={s.eyebrow}>
          {t("{scheduled} scheduled · {deferred} deferred · {undecided} undecided", {
            scheduled: p.totals.scheduled,
            deferred: p.totals.deferred,
            undecided: p.totals.undecidable,
          })}
        </div>

        {/* The number that turns "our data is incomplete" into something
            somebody funds. */}
        {blocked > 0 ? (
          <>
            <div className={`${s.big} ${s.blockedMoney}`}>{money(blocked, p.currency)}</div>
            <div className={s.reason}>
              {t(
                "of work is already priced and funded, and is held up by a missing fact rather than by money, weather or traffic. Every one of those is listed below with the single thing that would release it.",
              )}
            </div>
          </>
        ) : (
          <>
            <div className={s.big}>{money(p.totals.scheduledCost, p.currency)}</div>
            <div className={s.reason}>
              {t("of work is scheduled, and every candidate on the register reached a decision.")}
            </div>
          </>
        )}

        <div className={s.counts}>
          <div className={s.count}>
            <span>{t("Envelope")}</span>
            <b>{p.envelope != null ? money(p.envelope, p.currency) : "—"}</b>
          </div>
          <div className={s.count}>
            <span>{t("Committed")}</span>
            <b>{money(p.totals.scheduledCost, p.currency)}</b>
          </div>
          <div className={s.count}>
            <span>{t("Escalation if deferred")}</span>
            <b>{money(p.totals.deferredEscalation, p.currency)}</b>
          </div>
          {decided != null && (
            <div className={s.count}>
              {/* the honest headline: a programme that decided two of eight is
                  not an eight-job plan with six footnotes */}
              <span>{t("Register decided")}</span>
              <b>{Math.round(decided * 100)}%</b>
            </div>
          )}
        </div>

        {p.note && <div className={s.reason}>{p.note}</div>}
      </div>

      {p.scheduled.length > 0 && (
        <Card title={t("Scheduled — {n}", { n: p.scheduled.length })} sub={p.rankedBy}>
          <div className={s.jobs}>
            {p.scheduled.map((j) => (
              <div key={j.id} className={s.job}>
                <div className={s.jobHead}>
                  <span className={s.jobTitle}>{j.title}</span>
                  <span className={s.when}>
                    {formatDateTime(j.from)} → {formatDateTime(j.to)}
                  </span>
                </div>
                <div className={s.jobWhy}>
                  {money(j.cost, p.currency)}
                  {j.escalationAvoided
                    ? ` · ${t("avoids {amount} of escalation", { amount: money(j.escalationAvoided, p.currency) })}`
                    : ""}
                  {" · "}
                  {j.whyThen}
                </div>
                {/* a plan made on a forecast is reasonable; a plan made on a
                    forecast recorded as a measurement is unauditable */}
                {j.weatherBasis && j.weatherBasis.length > 0 && (
                  <div className={s.jobWhy}>{t("Weather from {sources}.", { sources: j.weatherBasis.join(", ") })}</div>
                )}
                <Why id={j.id} asking={asking} onAsk={setAsking} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {p.deferred.length > 0 && (
        <Card
          title={t("Deferred — {n}", { n: p.deferred.length })}
          sub="Decided, and the decision was to wait. The cost of waiting is stated on each."
        >
          <div className={s.jobs}>
            {p.deferred.map((j) => (
              <div key={j.id} className={`${s.job} ${s.deferredJob}`}>
                <div className={s.jobHead}>
                  <span className={s.jobTitle}>{j.title}</span>
                  <span className={s.when}>
                    {j.escalation ? t("+{amount} if it waits", { amount: money(j.escalation, p.currency) }) : ""}
                  </span>
                </div>
                <div className={s.jobWhy}>{j.why}</div>
                <Why id={j.id} asking={asking} onAsk={setAsking} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {desks.length > 0 && (
        <Card
          title={t("Could not be decided — {n}", { n: p.totals.undecidable })}
          sub="Grouped by who supplies the missing fact. Each row names one thing, and what it would release."
        >
          <div className={s.jobs}>
            {desks.map((d) => (
              <div className={s.desk} key={d.desk} data-testid={`desk-${d.desk}`}>
                <div className={s.deskHead}>
                  <span className={s.deskName}>{d.desk === "unassigned" ? t("unassigned") : d.desk}</span>
                  <span className={s.deskWorth}>
                    {d.worth > 0
                      ? t("{amount} of funded work", { amount: money(d.worth, p.currency) })
                      : tn(d.rows.length, "{n} candidate unrankable", "{n} candidates unrankable")}
                  </span>
                </div>
                {d.rows.map((r) => (
                  <div key={r.id} className={s.gap} data-testid={`gap-${r.id}`}>
                    <div className={s.gapTitle}>{r.title}</div>
                    <div className={s.gapMissing}>{r.missing}</div>
                    <div className={s.gapUnblocks}>
                      {t("Unblocks:")} {r.unblocks}
                    </div>
                    <Why id={r.id} asking={asking} onAsk={setAsking} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      {p.horizonNote && (
        <p className={s.reason}>
          {p.horizonNote}
          {p.searched
            ? ` ${t("Searched {from} to {to}.", { from: formatDateTime(p.searched.from), to: formatDateTime(p.searched.to) })}`
            : ""}
        </p>
      )}
    </>
  );
}

/**
 * "Why this?" — the evidence chain behind one recommendation.
 *
 * Collapsed by default and one at a time. The chain is a page of reading, and
 * a programme with eight of them open is a wall rather than a plan; the reader
 * asks the question about a specific line.
 */
function Why({ id, asking, onAsk }: { id: string; asking: string | null; onAsk: (id: string | null) => void }) {
  const open = asking === id;
  return (
    <>
      <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => onAsk(open ? null : id)}>
        {open ? t("Hide the evidence") : t("Why this?")}
      </Button>
      {open && <EvidenceChain candidateId={id} />}
    </>
  );
}
